import { createInertiaApp, router } from '@inertiajs/react'
import { createElement, type ComponentType, type ReactNode } from 'react'
import { trackPageView } from '../lib/analytics'

// Self-hosted fonts (bundled by Vite — no external requests).
import '@fontsource-variable/inter'
import '@fontsource/ibm-plex-sans-arabic/400.css'
import '@fontsource/ibm-plex-sans-arabic/500.css'
import '@fontsource/ibm-plex-sans-arabic/600.css'
import '@fontsource/ibm-plex-sans-arabic/700.css'

import { StoreLayout } from '../layouts/StoreLayout'
import { AdminLayout } from '../layouts/AdminLayout'
import { ErrorBoundary } from '../components/ErrorBoundary'

type LayoutFn = (page: ReactNode) => ReactNode
type PageComponent = ComponentType<Record<string, unknown>> & { layout?: LayoutFn }
type PageModule = { default: PageComponent }

// Wrap page content so a render crash shows a retry screen, never a blank page.
const withBoundary = (content: ReactNode) => createElement(ErrorBoundary, null, content)

// Fire a page view on every Inertia navigation (SPA).
router.on('navigate', (e) => trackPageView((e.detail.page.url as string) || window.location.pathname))

void createInertiaApp({
  // Code-split every page into its own chunk (performance budget).
  resolve: async (name) => {
    const pages = import.meta.glob<PageModule>('../pages/**/*.tsx')
    const page = await pages[`../pages/${name}.tsx`]()
    // Admin pages get AdminLayout (except the login screen); everything else
    // gets the StoreLayout. Every page is wrapped in an ErrorBoundary.
    if (page.default.layout === undefined) {
      if (name === 'admin/Login') {
        page.default.layout = (p: ReactNode) => withBoundary(p)
      } else if (name.startsWith('admin/')) {
        page.default.layout = (p: ReactNode) => withBoundary(createElement(AdminLayout, null, p))
      } else {
        page.default.layout = (p: ReactNode) => withBoundary(createElement(StoreLayout, null, p))
      }
    }
    return page.default
  },

  progress: { color: '#fe8011', showSpinner: false },

  strictMode: true,

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: 'brackets' }
    },
  },
}).catch((error) => {
  if (document.getElementById('app')) {
    throw error
  } else {
    console.error('Missing root element for Inertia app.')
  }
})
